import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Plus } from "@phosphor-icons/react"
import { t } from "i18next"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardBody,
  CardBodyContent,
  CardBodyHeader,
  CardBodyHeaderTitle,
  CardHeader,
  CardHeaderTitle,
} from "@/components/ui/card.tsx"
import useDialog from "@/hooks/useDialog"
import { fetchChPatient } from "@/lib/fetch-ch-patient"
import { DoctorInfo } from "@/modules/patients/components/doctor-info"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import AddDoctorNetworkDialog from "@/modules/patients/information/doctors-network/add-doctor-network-dialog"
import { chPatientByIdQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientByIdQuery.graphql"
import { useChDeleteDoctorNetworkMutation } from "@/modules/patients/services/ch/chDeleteDoctorNetworkMutation"
import { useConfirmStore } from "@/stores/useConfirmStore"

export type DoctorType = NonNullable<chPatientByIdQuery$data["patientById"]>["doctorNetworks"]

type Props = {
  doctors?: DoctorType
  isEditMode?: boolean
}

const DoctorsNetwork = ({ doctors, isEditMode = false }: Props) => {
  const { i18n } = useTranslation()
  const { open: isAddDoctorNetworkOpen, toggle: toggleAddDoctorNetwork } = useDialog()

  const patientId = usePatientParam()
  const country = useCountryParam()
  const cancel = useConfirmStore((state) => state.cancel)

  const [deleteDoctorNetwork] = useChDeleteDoctorNetworkMutation()

  const handleDelete = useCallback(
    (id: string) => {
      deleteDoctorNetwork({
        variables: { input: { id } },
        onCompleted: () => {
          fetchChPatient({ patientId, language: i18n.language })
          cancel()
        },
      })
    },
    [cancel, deleteDoctorNetwork, patientId, i18n],
  )

  const [currentDoctor, setCurrentDoctor] = useState<DoctorType>()

  useEffect(() => {
    setCurrentDoctor(doctors)
  }, [doctors, setCurrentDoctor])

  return (
    <Card className="animate-fade-in-top w-full" data-testid={"patient-information-doctorsnetwork"}>
      <CardHeader>
        <div className="flex items-center gap-4">
          <CardHeaderTitle data-testid="care-provider-card-title">
            {t("patient.information.doctorsNetwork")}
          </CardHeaderTitle>
        </div>
      </CardHeader>
      <CardBody>
        <CardBodyHeader className="pr-2 pl-4">
          <CardBodyHeaderTitle className="px-0">{t("patient.information.careProviders")}</CardBodyHeaderTitle>
          {isEditMode && country === "CH" && (
            <Button
              variant="circle"
              className="flex h-10 w-10 p-0"
              onClick={toggleAddDoctorNetwork}
              data-testid="care-provider-plus-button">
              <Plus className="text-on-surface-subdued" />
            </Button>
          )}
        </CardBodyHeader>
        <CardBodyContent className="flex-col gap-1 pb-4">
          {currentDoctor?.map((doctor) => (
            <DoctorInfo
              key={doctor.careProvider.id}
              canHover
              role={doctor.role}
              value={doctor.careProvider}
              id={doctor.id}
              onDelete={handleDelete}
              readOnly={!isEditMode}
              careProviderId={doctor.careProvider?.id}
            />
          ))}
        </CardBodyContent>
      </CardBody>
      <AddDoctorNetworkDialog doctors={doctors} open={isAddDoctorNetworkOpen} toggleDialog={toggleAddDoctorNetwork} />
    </Card>
  )
}
export { DoctorsNetwork }
